import { useState, useEffect, useRef } from 'react';
import { apiGet, apiPost, apiDelete } from '@/utils/api';
import { 
  isHumanHandoffRequest, 
  isFallbackResponse, 
  detectFrustration,
  initiateHumanHandoff,
  hideTidio
} from '@/utils/tidioService';

// Spending Insights Card Component
const SpendingInsightsCard = ({ data }) => {
  const { tier, insights = [], category_breakdown = [], total_current_month = 0, total_change = 0, upgrade_message } = data;
  
  if (tier === 'FREE') {
    return (
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xl">📊</span>
          <h4 className="font-semibold text-gray-900">Your Spending Overview</h4>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          Total this month: <span className="font-bold text-gray-900">${total_current_month.toFixed(2)}</span>
        </p>
        {category_breakdown.slice(0, 3).map((cat, idx) => (
          <div key={idx} className="flex justify-between text-sm py-1">
            <span className="text-gray-600 capitalize">{cat.category.replace('_', ' ')}</span>
            <span className="font-medium">${cat.current_month.toFixed(2)}</span>
          </div>
        ))}
        <div className="mt-4 p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg text-white text-sm">
          <span className="font-semibold">💎 Unlock Smart Insights!</span>
          <p className="text-xs mt-1 opacity-90">{upgrade_message}</p>
        </div>
      </div>
    );
  }
  
  // Premium tier - full insights
  return (
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xl">🧠</span>
        <h4 className="font-semibold text-gray-900">AI Spending Analysis</h4>
        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Premium</span>
      </div>
      
      {/* Summary */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-white rounded-lg">
        <div>
          <p className="text-xs text-gray-500">This Month</p>
          <p className="text-lg font-bold text-gray-900">${total_current_month.toFixed(2)}</p>
        </div>
        <div className={`flex items-center gap-1 ${total_change > 0 ? 'text-red-600' : 'text-green-600'}`}>
          {total_change > 0 ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
              <span className="text-sm font-medium">+${Math.abs(total_change).toFixed(0)}</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              <span className="text-sm font-medium">-${Math.abs(total_change).toFixed(0)}</span>
            </>
          )}
          <span className="text-xs text-gray-500">vs last month</span>
        </div>
      </div>
      
      {/* Smart Insights */}
      {insights.length > 0 && (
        <div className="space-y-2">
          {insights.map((insight, idx) => (
            <div 
              key={idx} 
              className={`p-3 rounded-lg text-sm ${
                insight.type === 'warning' ? 'bg-amber-50 border border-amber-200' :
                insight.type === 'success' ? 'bg-green-50 border border-green-200' :
                insight.type === 'celebration' ? 'bg-purple-50 border border-purple-200' :
                insight.type === 'alert' ? 'bg-red-50 border border-red-200' :
                insight.type === 'critical' ? 'bg-red-100 border border-red-300' :
                'bg-gray-50 border border-gray-200'
              }`}
            >
              <div className="flex items-start gap-2">
                <span className="text-lg">{insight.icon}</span>
                <div>
                  <p className="font-medium text-gray-900">{insight.title}</p>
                  <p className="text-gray-600 mt-1">{insight.message}</p>
                  {insight.action && (
                    <p className="text-xs mt-2 font-medium text-emerald-700">💡 {insight.action}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Category Breakdown */}
      {category_breakdown.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-medium text-gray-500 mb-2">TOP CATEGORIES</p>
          {category_breakdown.slice(0, 4).map((cat, idx) => (
            <div key={idx} className="flex items-center justify-between py-1.5 text-sm">
              <span className="text-gray-700 capitalize">{cat.category.replace('_', ' ')}</span>
              <div className="flex items-center gap-2">
                <span className="font-medium">${cat.current_month.toFixed(0)}</span>
                {cat.change !== 0 && (
                  <span className={`text-xs ${cat.change > 0 ? 'text-red-500' : 'text-green-500'}`}>
                    {cat.change > 0 ? '↑' : '↓'}${Math.abs(cat.change).toFixed(0)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Guapo Character Image URL
const GUAPO_IMAGE_URL = 'https://customer-assets.emergentagent.com/job_budget-buddy-4768/artifacts/qhory5fo_Guap.png';

// Guapo Icon Component (replaces Phoenix)
const GuapoIcon = ({ size = 40, className = '' }) => (
  <img 
    src={GUAPO_IMAGE_URL}
    alt="Guapo"
    width={size}
    height={size}
    className={`rounded-full object-cover ${className}`}
    style={{ width: size, height: size }}
  />
);

function AshChat({ isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [fallbackCount, setFallbackCount] = useState(0);
  const [handoffInProgress, setHandoffInProgress] = useState(false);
  const [spendingInsights, setSpendingInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load greeting when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadGreeting();
    }
    // Hide Tidio whenever Guapo chat opens to prevent overlap
    if (isOpen) {
      hideTidio();
    }
  }, [isOpen]);

  const loadGreeting = async () => {
    try {
      const response = await apiGet('/ash/greeting');
      if (response.ok) {
        const data = await response.json();
        setMessages([{
          role: 'assistant',
          content: data.greeting,
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      // Default greeting if API fails
      setMessages([{
        role: 'assistant',
        content: "Hello! 🔥 I'm Ash. What debt are we burning today?",
        timestamp: new Date()
      }]);
    }
  };

  // Fetch AI spending insights
  const fetchSpendingInsights = async () => {
    setLoadingInsights(true);
    setSpendingInsights(null);
    try {
      const response = await apiGet('/ash/spending-insights');
      if (response.ok) {
        const data = await response.json();
        setSpendingInsights(data);
        // Add a message about insights being loaded
        setMessages(prev => [...prev, {
          role: 'system',
          content: 'spending_insights',
          data: data,
          timestamp: new Date()
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "I couldn't load your spending insights right now. Try asking me about your spending habits! 💰",
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('Failed to fetch spending insights:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Oops! Had trouble analyzing your spending. Ask me anything else! 🔥",
        timestamp: new Date()
      }]);
    } finally {
      setLoadingInsights(false);
    }
  };

  // Handle human handoff sequence
  const triggerHumanHandoff = async (reason) => {
    if (handoffInProgress) return;
    setHandoffInProgress(true);
    
    console.log('Triggering human handoff:', reason);
    
    // Step 1: Acknowledgment message from Guapo
    const acknowledgmentMsg = {
      role: 'assistant',
      content: "Connecting you to a human now! 🤝 Please look for the chat icon in the corner.",
      timestamp: new Date()
    };
    setMessages(prev => [...prev, acknowledgmentMsg]);
    
    // Step 2: Small delay then trigger Tidio
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Get all messages for context
    const allMessages = [...messages, acknowledgmentMsg];
    
    // Safety check for Tidio API
    if (typeof window !== 'undefined' && window.tidioChatApi) {
      try {
        // Pass transcript to Tidio
        const transcript = allMessages.map(msg => {
          const role = msg.role === 'user' ? 'User' : 'Guapo';
          return `${role}: ${msg.content}`;
        }).join('\n');
        
        if (window.tidioChatApi.setContactProperties) {
          window.tidioChatApi.setContactProperties({
            guapo_chat_history: transcript,
            handoff_reason: reason,
            handoff_time: new Date().toISOString()
          });
        }
        
        // Display and open Tidio
        window.tidioChatApi.display(true);
        await new Promise(resolve => setTimeout(resolve, 300));
        window.tidioChatApi.open();
        
        console.log('Tidio opened successfully');
        
        // Close Guapo chat after Tidio opens
        setTimeout(() => {
          if (onClose) onClose();
        }, 500);
        
        return;
      } catch (error) {
        console.error('Tidio direct API failed:', error);
      }
    }
    
    // Fallback: Try using the service function
    const success = await initiateHumanHandoff(allMessages, onClose);
    
    console.log('Handoff result:', success);
    
    if (!success) {
      // Fallback if Tidio fails - show direct contact options
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Our live chat is connecting... If it doesn't open in a few seconds, you can reach us at:\n\n📧 Email: guapgoals26@gmail.com\n💬 Or try refreshing the page and asking again.",
        timestamp: new Date()
      }]);
      setHandoffInProgress(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading || handoffInProgress) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message immediately
    const userMsg = {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);

    // Check for explicit handoff request FIRST - don't send to backend
    console.log('Checking message for handoff:', userMessage);
    if (isHumanHandoffRequest(userMessage)) {
      console.log('Handoff request detected! Triggering Tidio...');
      await triggerHumanHandoff('explicit_request');
      return; // Don't continue to API call
    }

    // Check for frustration signals
    if (detectFrustration(userMessage)) {
      console.log('Frustration detected in message');
      // Don't immediately handoff, but be ready after one more fallback
      setFallbackCount(prev => prev + 1);
    }

    setLoading(true);
    try {
      const response = await apiPost('/ash/chat', {
        message: userMessage,
        session_id: sessionId
      });

      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        
        const botResponse = data.response;
        
        // Check if this is a fallback response
        if (isFallbackResponse(botResponse)) {
          const newFallbackCount = fallbackCount + 1;
          setFallbackCount(newFallbackCount);
          
          // If 2+ fallbacks, offer human handoff
          if (newFallbackCount >= 2) {
            // Add the bot response first
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: botResponse,
              timestamp: new Date()
            }]);
            
            // Then trigger handoff
            await triggerHumanHandoff('bot_failure');
            return;
          }
        } else {
          // Reset fallback count on successful response
          setFallbackCount(0);
        }
        
        // Add Guapo's response
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: botResponse,
          timestamp: new Date()
        }]);
      } else {
        throw new Error('Failed to get response');
      }
    } catch (error) {
      const newFallbackCount = fallbackCount + 1;
      setFallbackCount(newFallbackCount);
      
      if (newFallbackCount >= 2) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "I'm having some technical difficulties.",
          timestamp: new Date()
        }]);
        await triggerHumanHandoff('api_error');
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "Oops! My flames flickered for a moment. 🔥 Could you try again?",
          timestamp: new Date()
        }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    try {
      await apiDelete('/ash/history');
      setMessages([]);
      setSessionId(null);
      setFallbackCount(0);
      setHandoffInProgress(false);
      loadGreeting();
    } catch (error) {
      console.error('Failed to clear chat:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed bottom-24 right-4 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-50 overflow-hidden"
      style={{ height: '480px', maxHeight: 'calc(100vh - 140px)' }}
      data-testid="ash-chat-panel"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-600 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GuapoIcon size={44} />
          <div>
            <h3 className="font-bold text-white">Guapo</h3>
            <p className="text-xs text-emerald-300">Your Budget Buddy</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={clearChat}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            title="Clear chat"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-emerald-50/50 to-white">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {msg.role === 'system' && msg.content === 'spending_insights' ? (
              <div className="w-full">
                <SpendingInsightsCard data={msg.data} />
              </div>
            ) : (
              <div
                className={`max-w-[80%] p-3 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-br-md'
                    : 'bg-white border border-emerald-200 text-gray-800 rounded-bl-md shadow-sm'
                }`}
              >
                {msg.role === 'assistant' && (
                  <span className="text-emerald-500 mr-1">💰</span>
                )}
                {msg.content}
              </div>
            )}
          </div>
        ))}
        
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-emerald-200 p-3 rounded-2xl rounded-bl-md shadow-sm">
              <div className="flex items-center gap-2">
                <span className="text-emerald-500">💰</span>
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions */}
      <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 flex gap-2 overflow-x-auto">
        <button
          onClick={fetchSpendingInsights}
          disabled={loadingInsights || loading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-emerald-200 rounded-full text-xs font-medium text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 transition-all whitespace-nowrap disabled:opacity-50"
          data-testid="spending-insights-btn"
        >
          {loadingInsights ? (
            <>
              <span className="w-3 h-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></span>
              Analyzing...
            </>
          ) : (
            <>
              <span>📊</span>
              Spending Analysis
            </>
          )}
        </button>
        <button
          onClick={() => setInput('How can I save more money?')}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all whitespace-nowrap disabled:opacity-50"
        >
          <span>💡</span>
          Savings Tips
        </button>
        <button
          onClick={() => setInput('Help me with my debt payoff plan')}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all whitespace-nowrap disabled:opacity-50"
        >
          <span>☃️</span>
          Debt Help
        </button>
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="p-4 border-t border-gray-200 bg-white">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask Guapo anything..."
            disabled={loading}
            data-testid="ash-chat-input"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-100"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            data-testid="ash-send-btn"
            className="p-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full hover:from-emerald-600 hover:to-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </form>
    </div>
  );
}

// Floating button to open Ash chat
export function AshChatButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Show greeting tooltip on first render after login
  useEffect(() => {
    const hasSeenGreeting = sessionStorage.getItem('ash_greeted');
    if (!hasSeenGreeting) {
      const timer = setTimeout(() => {
        setShowTooltip(true);
        sessionStorage.setItem('ash_greeted', 'true');
        // Hide tooltip after 5 seconds
        setTimeout(() => setShowTooltip(false), 5000);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-6 right-4 z-40" data-testid="ash-chat-fab">
        {/* Tooltip */}
        {showTooltip && !isOpen && (
          <div className="absolute bottom-20 right-0 bg-white rounded-xl shadow-lg border border-emerald-200 p-3 w-64 animate-bounce-gentle">
            <div className="flex items-start gap-2">
              <GuapoIcon size={28} />
              <div>
                <p className="font-medium text-gray-900">Hey! I'm Guapo</p>
                <p className="text-sm text-gray-600">Let's get your money right!</p>
              </div>
            </div>
            <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-emerald-200 transform rotate-45"></div>
          </div>
        )}
        
        <button
          onClick={() => {
            setIsOpen(!isOpen);
            setShowTooltip(false);
          }}
          className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all transform hover:scale-110 ${
            isOpen 
              ? 'bg-gray-600' 
              : 'bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600'
          }`}
          style={{
            boxShadow: isOpen ? undefined : '0 0 20px rgba(16, 185, 129, 0.6), 0 0 40px rgba(20, 184, 166, 0.3)'
          }}
        >
          {isOpen ? (
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <GuapoIcon size={42} />
          )}
        </button>
      </div>

      {/* Chat Panel */}
      <AshChat isOpen={isOpen} onClose={() => setIsOpen(false)} />

      {/* Custom animation styles */}
      <style jsx>{`
        @keyframes bounce-gentle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-bounce-gentle {
          animation: bounce-gentle 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
}

export default AshChat;
